import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { initGA } from "@/lib/analytics";
import { Cookie } from "lucide-react";

export function CookieConsent() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];
  const isRTL = i18n.dir() === "rtl";
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setShowBanner(true);
      // Prevent scrolling
      document.body.style.overflow = "hidden";
    }
  }, []);

  const removeBanner = () => {
    document.body.style.overflow = "auto";
    setShowBanner(false);
  };

  const handleAccept = () => {
    // Initialize Google Analytics when user accepts cookies
    initGA();

    // Store consent in localStorage
    localStorage.setItem("cookieConsent", "accepted");
    localStorage.setItem("analyticsEnabled", "true");

    // Remove banner
    removeBanner();
  };

  const handleDecline = () => {
    // Store decline in localStorage
    localStorage.setItem("cookieConsent", "declined");
    localStorage.setItem("analyticsEnabled", "false");

    // Remove banner
    removeBanner();
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="cookie-consent-root fixed inset-0 z-[99999] flex items-center justify-center p-4">
      {/* Backdrop overlay */}
      <div
        className="cookie-consent-backdrop absolute inset-0 bg-black/70"
        aria-hidden="true"
      />

      {/* Modal - on top of backdrop */}
      <div
        className="cookie-consent-modal relative w-full max-w-2xl bg-card rounded-lg shadow-2xl p-6 sm:p-8 border border-primary/20"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center shrink-0 ring-2 ring-primary/30">
            <Cookie className="w-7 h-7 text-primary" aria-hidden="true" />
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <h2 className="text-xl font-bold text-foreground">
              {t("cookieConsent.title")}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("cookieConsent.message")}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <button
            onClick={handleAccept}
            className="cookie-consent-accept flex-1 bg-primary text-primary-foreground text-sm font-semibold py-3 px-6 rounded-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
          >
            {t("cookieConsent.accept")}
          </button>
          <button
            onClick={handleDecline}
            className="flex-1 bg-secondary text-foreground text-sm font-medium py-3 px-6 rounded-lg border border-border hover:bg-secondary/80 transition-all"
          >
            {t("cookieConsent.decline")}
          </button>
        </div>

        <a
          href={`/${lang}/privacy`}
          className="text-xs text-primary hover:text-primary/80 font-medium inline-flex items-center gap-1 transition-colors"
        >
          {t("cookieConsent.learnMore")} →
        </a>
      </div>
    </div>
  );
}
